/**
 * OutcomeTagging - Outcome tagging UI for calls
 *
 * Features:
 * - Predefined system tags (Sale, Appointment Set, etc.)
 * - Multiple tags per call
 * - Color-coded badges
 * - Quick tag selection
 */
import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Tag, Plus, X, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface CallTag {
  id: string;
  name: string;
  color: string;
  is_system: boolean;
}

interface TagAssignment {
  tag_id: string;
  tag: CallTag;
}

export interface OutcomeTaggingProps {
  /** The call ID to tag */
  callId: string;
  /** Whether to show as compact inline badges */
  compact?: boolean;
  /** Callback when tags change */
  onTagsChange?: (tagIds: string[]) => void;
}

export function OutcomeTagging({
  callId,
  compact = false,
  onTagsChange,
}: OutcomeTaggingProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  // Fetch available tags for the tenant
  const { data: availableTags = [], isLoading: loadingTags } = useQuery({
    queryKey: ['call-tags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calie_call_tags')
        .select('*')
        .order('is_system', { ascending: false })
        .order('name');

      if (error) throw error;
      return data as CallTag[];
    },
  });

  // Fetch tags assigned to this call
  const { data: assignedTags = [], isLoading: loadingAssigned } = useQuery({
    queryKey: ['call-tag-assignments', callId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calie_call_tag_assignments')
        .select('tag_id, tag:calie_call_tags(*)')
        .eq('call_id', callId);

      if (error) throw error;
      return (data || []).map((d: any) => ({
        tag_id: d.tag_id,
        tag: d.tag as CallTag,
      })) as TagAssignment[];
    },
  });

  const assignedTagIds = assignedTags.map((t) => t.tag_id);
  const prevTagIdsRef = useRef<string[]>([]);

  // Assign tag mutation
  const assignMutation = useMutation({
    mutationFn: async (tagId: string) => {
      const { error } = await supabase
        .from('calie_call_tag_assignments')
        .insert({
          call_id: callId,
          tag_id: tagId,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['call-tag-assignments', callId] });
    },
    onError: (error) => {
      console.error('[OutcomeTagging] Assign error:', error);
      toast.error('Failed to add tag');
    },
  });

  // Remove tag mutation
  const removeMutation = useMutation({
    mutationFn: async (tagId: string) => {
      const { error } = await supabase
        .from('calie_call_tag_assignments')
        .delete()
        .eq('call_id', callId)
        .eq('tag_id', tagId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['call-tag-assignments', callId] });
    },
    onError: (error) => {
      console.error('[OutcomeTagging] Remove error:', error);
      toast.error('Failed to remove tag');
    },
  });

  // Notify parent of changes (only when tags actually change)
  useEffect(() => {
    const prevIds = prevTagIdsRef.current;
    const idsChanged =
      prevIds.length !== assignedTagIds.length ||
      !assignedTagIds.every((id) => prevIds.includes(id));

    if (idsChanged) {
      prevTagIdsRef.current = assignedTagIds;
      onTagsChange?.(assignedTagIds);
    }
  }, [assignedTagIds, onTagsChange]);

  const handleToggleTag = (tagId: string) => {
    if (assignedTagIds.includes(tagId)) {
      removeMutation.mutate(tagId);
    } else {
      assignMutation.mutate(tagId);
    }
  };

  const handleRemoveTag = (tagId: string) => {
    removeMutation.mutate(tagId);
  };

  const isLoading = loadingTags || loadingAssigned;
  const isMutating = assignMutation.isPending || removeMutation.isPending;

  // Compact inline view (for use in tables/lists)
  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-1">
        {assignedTags.map((assignment) => (
          <Badge
            key={assignment.tag_id}
            variant="secondary"
            style={{ backgroundColor: `${assignment.tag.color}20`, color: assignment.tag.color }}
            className="text-xs"
          >
            {assignment.tag.name}
          </Badge>
        ))}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
              <Plus className="h-3 w-3" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[200px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search tags..." />
              <CommandList>
                <CommandEmpty>No tags found.</CommandEmpty>
                <CommandGroup>
                  {availableTags.map((tag) => (
                    <CommandItem
                      key={tag.id}
                      onSelect={() => handleToggleTag(tag.id)}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: tag.color }}
                        />
                        <span>{tag.name}</span>
                      </div>
                      {assignedTagIds.includes(tag.id) && (
                        <Check className="h-4 w-4" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  // Full card view
  return (
    <Card>
      <CardHeader className="p-4 sm:p-6">
        <CardTitle className="text-base sm:text-lg flex items-center gap-2">
          <Tag className="h-4 w-4" />
          Outcome Tags
        </CardTitle>
        <CardDescription>
          Tag this call with outcomes for tracking and reporting
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0 space-y-4">
        {/* Currently assigned tags */}
        {assignedTags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {assignedTags.map((assignment) => (
              <Badge
                key={assignment.tag_id}
                variant="secondary"
                style={{
                  backgroundColor: `${assignment.tag.color}20`,
                  color: assignment.tag.color,
                  borderColor: assignment.tag.color,
                }}
                className="text-sm py-1 px-3 border"
              >
                {assignment.tag.name}
                <button
                  onClick={() => handleRemoveTag(assignment.tag_id)}
                  className="ml-2 hover:opacity-70"
                  disabled={isMutating}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        {/* Tag selector */}
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            {assignedTags.length === 0 ? 'Add tags:' : 'Add more tags:'}
          </p>

          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading tags...
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {availableTags
                .filter((tag) => !assignedTagIds.includes(tag.id))
                .map((tag) => (
                  <Button
                    key={tag.id}
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleTag(tag.id)}
                    disabled={isMutating}
                    className="gap-2"
                    style={{ borderColor: tag.color }}
                  >
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: tag.color }}
                    />
                    {tag.name}
                  </Button>
                ))}
            </div>
          )}
        </div>

        {/* All tags assigned message */}
        {!isLoading &&
          availableTags.length > 0 &&
          availableTags.every((tag) => assignedTagIds.includes(tag.id)) && (
            <p className="text-sm text-muted-foreground">
              All available tags have been assigned.
            </p>
          )}
      </CardContent>
    </Card>
  );
}

/**
 * OutcomeTagBadges - Read-only display of outcome tags
 * For use in tables and lists where editing isn't needed
 */
export interface OutcomeTagBadgesProps {
  /** Tags to display */
  tags: Array<{ name: string; color: string }>;
  /** Maximum tags to show before "+N more" */
  maxVisible?: number;
}

export function OutcomeTagBadges({
  tags,
  maxVisible = 3,
}: OutcomeTagBadgesProps) {
  if (!tags || tags.length === 0) {
    return <span className="text-muted-foreground text-sm">-</span>;
  }

  const visibleTags = tags.slice(0, maxVisible);
  const remainingCount = tags.length - maxVisible;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visibleTags.map((tag, index) => (
        <Badge
          key={index}
          variant="secondary"
          style={{
            backgroundColor: `${tag.color}20`,
            color: tag.color,
          }}
          className="text-xs"
        >
          {tag.name}
        </Badge>
      ))}
      {remainingCount > 0 && (
        <Badge variant="outline" className="text-xs">
          +{remainingCount} more
        </Badge>
      )}
    </div>
  );
}
